// ---------------------------------------------------------------------------
// OCV → SIG Format Converter
// Converts OCV (Online Content Validation) JSON payloads to the SIG
// (Substrate Signal) format so the debug page can display both uniformly.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// OCV type definitions (input)
// ---------------------------------------------------------------------------

interface OcvEnvironmentContext {
  DeployRing?: string;
  Region?: string;
  CosmicPartition?: string;
  CosmicAppName?: string;
  CosmicLocation?: string;
  RoleInstance?: string;
  SourceBranch?: string;
}

interface OcvExecutionContext {
  AppName?: string;
  AppScenario?: string;
  ClientPlatform?: string;
  ClientAppName?: string;
  OptionSets?: string[];
  Variants?: string[];
  IsTestRequest?: boolean;
  TurnIndex?: string;
  Locale?: string;
  Market?: string;
  Region?: string;
}

interface OcvAdditionalProperties {
  InteractionId?: string;
  AgentType?: string;
  HostApp?: string;
  ServiceVersion?: string;
  [key: string]: unknown;
}

interface OcvLlmPromptResponseSignal {
  StartTime?: string;
  EndTime?: string;
  EnvironmentContext?: OcvEnvironmentContext;
  ExecutionContext?: OcvExecutionContext;
  AdditionalProperties?: OcvAdditionalProperties;
}

interface OcvConversationHistoryEntry {
  TurnIndex?: number;
  Utterance?: string;
  Response?: string;
  ReferencedDocuments?: string;
  EvaluationMetrics?: string;
}

interface OcvPayload {
  OcvType?: string;
  LlmPromptResponseSignals?: OcvLlmPromptResponseSignal[];
  LlmPromptInteractionSignals?: unknown[];
  ConversationHistory?: OcvConversationHistoryEntry[];
  OcvReferenceId?: string;
  OcvReferenceType?: string;
  OcvReferenceCreatedDate?: string;
  id?: string;
}

// ---------------------------------------------------------------------------
// SIG type definitions (output) — mirrors Substrate signal shape
// ---------------------------------------------------------------------------

interface SigExecutionEvent {
  EventType: string;
  [key: string]: unknown;
}

interface SigSignal {
  Id: string;
  SignalType: string;
  CV: string;
  CreationTime: string;
  StartTime: string;
  EndTime: string;
  Status: string;
  Application: {
    AadAppId: string;
    AppName: string;
    AppVer: string;
    Workload: string;
  };
  Actor: {
    AadTenantId: string;
    ActorId: string;
    ActorType: string;
  };
  Device: {
    UserAgent: string;
  };
  Item: {
    ItemId: string;
  };
  CustomProperties: {
    CompressedData: {
      ExecutionContext: Record<string, unknown>;
      ExecutionEvents: SigExecutionEvent[];
      EnvironmentContext: Record<string, unknown>;
    };
  };
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/** Returns true if the parsed JSON looks like an OCV payload */
export function isOcvPayload(obj: Record<string, unknown>): boolean {
  return (
    'OcvType' in obj &&
    'LlmPromptResponseSignals' in obj &&
    Array.isArray(obj.LlmPromptResponseSignals)
  );
}

// ---------------------------------------------------------------------------
// Converter
// ---------------------------------------------------------------------------

/**
 * Deterministically converts an OCV JSON payload to an array of SIG-format
 * signal objects that the existing `parseSigsInput` / `parseSignal` can handle.
 *
 * Each ConversationHistory entry becomes a separate signal so multi-turn
 * interactions display as a list of signals in the debug page.
 */
export function convertOcvToSig(ocv: OcvPayload): Record<string, unknown>[] {
  const responseSignal = ocv.LlmPromptResponseSignals?.[0];
  const execCtx = responseSignal?.ExecutionContext ?? {};
  const envCtx = responseSignal?.EnvironmentContext ?? {};
  const startTime = responseSignal?.StartTime ?? '';
  const endTime = responseSignal?.EndTime ?? '';

  const conversationHistory = ocv.ConversationHistory ?? [];

  // Generate a deterministic ID from the OCV reference
  const baseId = ocv.OcvReferenceId ?? ocv.id ?? 'ocv-unknown';

  // If there's no conversation history, produce a single signal from the
  // response signal metadata alone
  if (conversationHistory.length === 0) {
    return [
      buildSigFromOcv({
        baseId,
        turnIndex: 0,
        startTime,
        endTime,
        execCtx,
        envCtx,
        utterance: '',
        response: '',
        referencedDocuments: '',
        evaluationMetrics: '',
      }) as unknown as Record<string, unknown>,
    ];
  }

  return conversationHistory.map(
    (entry, idx) =>
      buildSigFromOcv({
        baseId,
        turnIndex: entry.TurnIndex ?? idx,
        startTime,
        endTime,
        execCtx,
        envCtx,
        utterance: entry.Utterance ?? '',
        response: entry.Response ?? '',
        referencedDocuments: entry.ReferencedDocuments ?? '',
        evaluationMetrics:
          typeof entry.EvaluationMetrics === 'string'
            ? entry.EvaluationMetrics
            : JSON.stringify(entry.EvaluationMetrics ?? ''),
      }) as unknown as Record<string, unknown>,
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface BuildSigParams {
  baseId: string;
  turnIndex: number;
  startTime: string;
  endTime: string;
  execCtx: OcvExecutionContext;
  envCtx: OcvEnvironmentContext;
  utterance: string;
  response: string;
  referencedDocuments: string;
  evaluationMetrics: string;
}

function buildSigFromOcv(params: BuildSigParams): SigSignal {
  const {
    baseId,
    turnIndex,
    startTime,
    endTime,
    execCtx,
    envCtx,
    utterance,
    response,
    referencedDocuments,
    evaluationMetrics,
  } = params;

  const itemId = `${baseId}_turn${turnIndex}`;

  const executionEvents: SigExecutionEvent[] = [
    {
      EventType: 'AugloopContextExecutionEvent',
      ServerSessionId: '',
      LlmScenarioName: execCtx.AppScenario ?? '',
      LlmScenarioId: '',
      ClientSessionId: '',
      ClientAppName: execCtx.ClientAppName ?? '',
    },
    {
      EventType: 'CopilotChatExecutionEvent',
      UserPrompt: utterance,
      Response: response,
      ReferencedDocuments: referencedDocuments,
      ConversationId: '',
      TurnId: String(turnIndex),
    },
    {
      EventType: 'EvaluationMetricsExecutionEvent',
      EvaluationMetrics: evaluationMetrics,
    },
  ];

  return {
    Id: itemId,
    SignalType: 'LlmPromptResponse',
    CV: `ocv-${baseId}`,
    CreationTime: startTime,
    StartTime: startTime,
    EndTime: endTime,
    Status: 'Completed',
    Application: {
      AadAppId: '',
      AppName: execCtx.AppName ?? '',
      AppVer: '',
      Workload: '',
    },
    Actor: {
      AadTenantId: '',
      ActorId: '',
      ActorType: '',
    },
    Device: {
      UserAgent: '',
    },
    Item: {
      ItemId: itemId,
    },
    CustomProperties: {
      CompressedData: {
        ExecutionContext: {
          AppName: execCtx.AppName ?? '',
          AppScenario: execCtx.AppScenario ?? '',
          ClientPlatform: execCtx.ClientPlatform ?? '',
          ClientAppName: execCtx.ClientAppName ?? '',
          OptionSets: execCtx.OptionSets ?? [],
          Variants: execCtx.Variants ?? [],
          IsTestRequest: execCtx.IsTestRequest ?? false,
          TurnIndex: String(turnIndex),
          Locale: execCtx.Locale ?? '',
          Market: execCtx.Market ?? '',
          Region: execCtx.Region ?? '',
        },
        ExecutionEvents: executionEvents,
        EnvironmentContext: {
          DeployRing: envCtx.DeployRing ?? '',
          Region: envCtx.Region ?? '',
          CosmicPartition: envCtx.CosmicPartition ?? '',
          CosmicAppName: envCtx.CosmicAppName ?? '',
          CosmicLocation: envCtx.CosmicLocation ?? '',
          RoleInstance: envCtx.RoleInstance ?? '',
          SourceBranch: envCtx.SourceBranch ?? '',
        },
      },
    },
  };
}
